import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LinkTemporalIcon from '../../../assets/images/link-temporal.svg';
import WaveIcon from '../../../assets/images/wave-icon.svg';

export const GetStartedStep = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);
  const prevRef = useRef(activeIndex);

  const options = [
    { 
      name: 'LINK ESCUCHA', 
      icon: LinkTemporalIcon,
      description: ''
    },
    { 
      name: 'ADELANTOS (VIDEO)', 
      icon: WaveIcon,
      description: ''
    }
  ];

  useEffect(() => {
    prevRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const startAnimation = () => {
      // First highlight
      setTimeout(() => {
        setActiveIndex(1); // Switch to second option after 3 seconds
        // Then loop back
        setTimeout(() => {
          setActiveIndex(0); // Back to first option
        }, 3000);
      }, 3000);
    };

    startAnimation();
    
    // Loop every 6 seconds
    const interval = setInterval(startAnimation, 6000);

    return () => clearInterval(interval);
  }, []);

  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Highlight animation variants
  const ENTER_DURATION = 1.2;
  const LEAVE_DURATION = 1.2;
  const OVERLAP_DELAY = 0.4;

  // Función para determinar la dirección del movimiento
  const getDirection = (currentIndex, previousIndex) => {
    if (previousIndex === 0 && currentIndex === 1) return 'down'; // Primera a segunda: arriba → abajo
    if (previousIndex === 1 && currentIndex === 0) return 'up';   // Segunda a primera: abajo → arriba
    return 'down'; // Por defecto
  };

  const direction = getDirection(activeIndex, prevRef.current);

  const enterVariants = {
    initial: { y: direction === 'down' ? "-100%" : "100%", opacity: 0.9 },
    animate: { 
      y: "0%", 
      opacity: 1, 
      transition: { 
        duration: ENTER_DURATION, 
        ease: [0.22, 1, 0.36, 1],
        delay: OVERLAP_DELAY 
      } 
    },
    exit: { 
      y: direction === 'down' ? "100%" : "-100%", 
      opacity: 0, 
      transition: { 
        duration: LEAVE_DURATION, 
        ease: [0.22, 1, 0.36, 1] 
      } 
    },
  };

  const leaveVariants = {
    initial: { y: "0%", opacity: 1 },
    animate: { 
      y: direction === 'down' ? "100%" : "-100%", 
      opacity: 0, 
      transition: { 
        duration: LEAVE_DURATION, 
        ease: [0.22, 1, 0.36, 1] 
      } 
    },
  };

  // Mobile: Contenido encolumnado
  if (isMobile) {
    return (
      <motion.div 
        className="flex flex-col justify-center items-center space-y-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {options.map((option, index) => (
          <motion.div
            key={index}
            className="relative w-full max-w-[265px] rounded-[24px] py-6 px-6 text-center outline-none overflow-hidden bg-transparent"
            variants={cardVariants}
            style={{ isolation: 'isolate' }}
          >
            {/* Highlight entrante */}
            <AnimatePresence initial={false} mode="popLayout">
              {index === activeIndex && (
                <motion.div
                  key="incoming"
                  variants={enterVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0 bg-[#FFB447] rounded-[24px] z-10"
                />
              )}
            </AnimatePresence>

            {/* Highlight saliente */}
            <AnimatePresence initial={false}>
              {index === prevRef.current && prevRef.current !== activeIndex && (
                <motion.div
                  key="leaving"
                  variants={leaveVariants}
                  initial="initial"
                  animate="animate"
                  exit="animate"
                  className="absolute inset-0 bg-[#FFB447] rounded-[24px] z-10"
                />
              )}
            </AnimatePresence>

            {/* Content */}
            <div className="relative z-20">
              <motion.img
                src={option.icon}
                alt={option.name}
                className={`mx-auto transition-all duration-300 ${
                  index === 0 ? 'w-[62px] h-[72px]' : 'w-[96px] h-[72px] '
                }`}
                style={{
                  filter: index === activeIndex ? 'brightness(0) saturate(100%) invert(0%)' : 
                         'brightness(0) saturate(100%) invert(77%) sepia(6%) saturate(318%) hue-rotate(183deg) brightness(96%) contrast(86%)'
                }}
              />
              <h3 className={`text-[34px] font-thunder font-semibold tracking-wider whitespace-nowrap transition-colors duration-300 ${
                index === activeIndex ? 'text-black' : 'text-[#C4C9CF]'
              }`}>
                {option.name}
              </h3>
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  }

  // Desktop: Layout 50/50
  return (
    <div className="flex-1 flex px-2">
      {/* Texto del lado izquierdo - 50% */}
      <div className="w-1/2 flex items-center">
        <h1 className="text-white !font-[500] text-[34px] leading-[38px] !font-archivo normal-case">
          Compartí tu música de la forma que más te guste
        </h1>
      </div>
      
      {/* Contenedor derecho con opciones - 50% */}
      <motion.div 
        className="w-1/2 flex flex-col justify-center items-center relative pl-2 space-y-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {options.map((option, index) => {
          const isActive = activeIndex === index;
          
          return (
            <motion.div
              key={index}
              className="relative w-full max-w-[190px] h-[132px] rounded-[24px] py-4 px-6 text-center outline-none overflow-hidden bg-transparent"
              variants={cardVariants}
              style={{ isolation: 'isolate' }}
            >
              {/* Highlight entrante */}
              <AnimatePresence initial={false} mode="popLayout">
                {index === activeIndex && (
                  <motion.div
                    key="incoming"
                    variants={enterVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute inset-0 bg-[#FFB447] rounded-[24px] z-10"
                  />
                )}
              </AnimatePresence>

              {/* Highlight saliente */}
              <AnimatePresence initial={false}>
                {index === prevRef.current && prevRef.current !== activeIndex && (
                  <motion.div
                    key="leaving"
                    variants={leaveVariants}
                    initial="initial"
                    animate="animate"
                    exit="animate"
                    className="absolute inset-0 bg-[#FFB447] rounded-[24px] z-10"
                  />
                )}
              </AnimatePresence>

              {/* Content */}
              <div className="relative z-20">
                <motion.img
                  src={option.icon}
                  alt={option.name}
                  className={`mx-auto transition-all duration-300 ${
                    index === 0 ? 'w-[58px] h-[58px]' : 'w-[76px] h-[76px] -mb-2'
                  }`}
                  style={{
                    filter: index === activeIndex ? 'brightness(0) saturate(100%) invert(0%)' : 
                           'brightness(0) saturate(100%) invert(77%) sepia(6%) saturate(318%) hue-rotate(183deg) brightness(96%) contrast(86%)'
                  }}
                />
                <h3 className={`text-[24px] font-thunder text-center font-semibold tracking-wider whitespace-nowrap transition-colors duration-300 ${
                  index === activeIndex ? 'text-black' : 'text-[#C4C9CF]'
                }`}>
                  {option.name}
                </h3>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

    </div>
  );
};